class TeePantsBundle extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.status = this.querySelector('[data-status]');
    if (!this.status) return;
    try {
      this.initialize();
    } catch {
      this.status.textContent = 'Unable to load bundle options. Please reload the page.';
    }
    const cardStatus = this.querySelector('[data-card-status]');
    if (cardStatus) cardStatus.textContent = this.trigger?.disabled ? this.status.textContent : '';
  }

  initialize() {
    this.products = JSON.parse(this.querySelector('[data-bundle-products]').textContent);
    this.teeSelect = this.querySelector('[data-tee]');
    this.teeSize = this.querySelector('[data-tee-size]');
    this.pantsColor = this.querySelector('[data-pants-color]');
    this.pantsSize = this.querySelector('[data-pants-size]');
    this.button = this.querySelector('button[type="submit"]');
    this.status = this.querySelector('[data-status]');
    this.dialog = this.querySelector('dialog');
    this.trigger = this.querySelector('[data-open-combo]');
    const { signal } = this.controller;
    this.trigger?.addEventListener('click', () => {
      this.dialog.showModal();
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.scrollLocked = true;
    }, { signal });
    this.querySelector('[data-close-combo]')?.addEventListener('click', () => this.dialog.close(), { signal });
    this.dialog?.addEventListener('close', () => this.restoreScroll(), { signal });
    this.dialog?.addEventListener('click', (event) => {
      if (event.target !== this.dialog) return;
      const bounds = this.dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right
        || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
    }, { signal });

    if (!Array.isArray(this.products.tees) || !this.products.tees.length || !this.products.pants) {
      this.status.textContent = this.dataset.designMode === 'true'
        ? 'Select the eligible tees and pants in the bundle block settings.'
        : 'This bundle is currently unavailable.';
      return;
    }

    const optionIndex = (product, names) => product.options.findIndex((option) =>
      names.includes((typeof option === 'string' ? option : option.name).trim().toLowerCase())
    );
    this.pantsColorIndex = optionIndex(this.products.pants, ['color', 'colour']);
    this.pantsSizeIndex = optionIndex(this.products.pants, ['size']);
    this.teeSizeIndices = this.products.tees.map((tee) => optionIndex(tee, ['size']));
    if (this.pantsColorIndex < 0 || this.pantsSizeIndex < 0 || this.teeSizeIndices.includes(-1)) {
      this.status.textContent = this.dataset.designMode === 'true'
        ? 'Tees need a Size option; pants need Color and Size options.'
        : 'This bundle is currently unavailable.';
      return;
    }

    this.setOptions(this.teeSelect, this.products.tees.map((tee, index) => ({
      value: String(index), label: tee.title, available: tee.variants.some((variant) => variant.available),
    })));
    this.setVariantOptions(this.pantsColor, this.products.pants.variants, this.pantsColorIndex);
    this.updateTee();
    this.updatePants();
    if (this.trigger) this.trigger.disabled = false;
    this.teeSelect.addEventListener('change', () => this.updateTee(), { signal });
    this.teeSize.addEventListener('change', () => this.updateSelection(), { signal });
    this.pantsColor.addEventListener('change', () => this.updatePants(), { signal });
    this.pantsSize.addEventListener('change', () => this.updateSelection(), { signal });
    this.querySelector('form').addEventListener('submit', (event) => this.addBundle(event), { signal });
  }

  disconnectedCallback() {
    this.dialog?.close();
    this.restoreScroll();
    this.controller?.abort();
  }

  restoreScroll() {
    if (!this.scrollLocked) return;
    document.documentElement.style.overflow = this.previousOverflow;
    this.scrollLocked = false;
  }

  setOptions(select, choices) {
    const previous = select.value;
    select.replaceChildren(...choices.map((choice) => {
      const option = new Option(choice.label, choice.value);
      option.disabled = !choice.available;
      return option;
    }));
    const selected = choices.find((choice) => choice.value === previous && choice.available)
      ?? choices.find((choice) => choice.available);
    select.value = selected?.value ?? '';
    select.disabled = !selected;
  }

  setVariantOptions(select, variants, index) {
    const values = [...new Set(variants.map((variant) => variant.options[index]))];
    this.setOptions(select, values.map((value) => ({
      value, label: value,
      available: variants.some((variant) => variant.options[index] === value && variant.available),
    })));
  }

  updateTee() {
    const tee = this.products.tees[Number(this.teeSelect.value)];
    this.setVariantOptions(this.teeSize, tee.variants, this.teeSizeIndices[Number(this.teeSelect.value)]);
    this.updateSelection();
  }

  updatePants() {
    const variants = this.products.pants.variants.filter((variant) =>
      variant.options[this.pantsColorIndex] === this.pantsColor.value
    );
    this.setVariantOptions(this.pantsSize, variants, this.pantsSizeIndex);
    this.updateSelection();
  }

  updateSelection() {
    const teeIndex = Number(this.teeSelect.value);
    const tee = this.products.tees[teeIndex];
    this.teeVariant = this.teeSelect.value === '' ? undefined : tee.variants.find((variant) =>
      variant.available && variant.options[this.teeSizeIndices[teeIndex]] === this.teeSize.value
    );
    this.pantsVariant = this.products.pants.variants.find((variant) => variant.available
      && variant.options[this.pantsColorIndex] === this.pantsColor.value
      && variant.options[this.pantsSizeIndex] === this.pantsSize.value
    );
    this.updatePreview('tee', tee, this.teeVariant, this.teeSize.value);
    this.updatePreview('pants', this.products.pants, this.pantsVariant,
      [this.pantsColor.value, this.pantsSize.value].filter(Boolean).join(' / '));
    this.button.disabled = this.loading || !this.teeVariant || !this.pantsVariant;
    this.status.textContent = this.teeVariant && this.pantsVariant ? '' : 'This combination is sold out. Please choose another option.';
  }

  updatePreview(kind, product, variant, selection) {
    const source = variant?.image || product.image;
    this.querySelectorAll(`[data-${kind}-image]`).forEach((image) => {
      image.hidden = !source;
      if (source) {
        image.src = source;
        image.alt = `${product.title}${selection ? ` - ${selection}` : ''}`;
      } else {
        image.removeAttribute('src');
      }
    });
    const summary = this.querySelector(`[data-${kind}-summary]`);
    if (summary) summary.textContent = kind === 'tee'
      ? `${product.title}${selection ? ` / ${selection}` : ''}`
      : selection || product.title;
  }

  async addBundle(event) {
    event.preventDefault();
    if (this.loading || !this.teeVariant || !this.pantsVariant) return;
    this.loading = true;
    this.button.disabled = true;
    this.button.textContent = 'Adding bundle…';
    this.status.textContent = '';
    this.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${this.dataset.rootUrl}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [
          { id: this.teeVariant.id, quantity: 1 },
          { id: this.pantsVariant.id, quantity: 1 },
        ] }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : 'Unable to add your bundle. Please try again.');
      const discount = this.dataset.discount.trim();
      window.location.assign(discount
        ? `${this.dataset.rootUrl}discount/${encodeURIComponent(discount)}?redirect=${encodeURIComponent(this.dataset.cartUrl)}`
        : this.dataset.cartUrl);
    } catch (error) {
      this.status.textContent = error.message || 'Unable to add your bundle. Please try again.';
    } finally {
      this.loading = false;
      this.button.disabled = !this.teeVariant || !this.pantsVariant;
      this.button.textContent = 'Add combo to cart';
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('tee-pants-bundle')) {
  customElements.define('tee-pants-bundle', TeePantsBundle);
}
